import React, { useState, useEffect } from 'react';
import apiClient from '../api';

function MemoEditor({ memoToEdit, onSave, onCancel, onManageCategories }) {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category_id: null
  });
  
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  useEffect(() => {
    if (memoToEdit) {
      setFormData({
        title: memoToEdit.title || '',
        content: memoToEdit.content || '',
        category_id: memoToEdit.category_id || null
      });
    } else {
      setFormData({ title: '', content: '', category_id: null });
    }
  }, [memoToEdit]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await apiClient.get('/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('获取分类失败:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategorySelect = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      category_id: categoryId
    }));
    setShowCategoryDropdown(false);
  };

  const handleRemoveCategory = () => {
    setFormData(prev => ({
      ...prev,
      category_id: null
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('标题不能为空');
      return;
    }
    onSave(formData);
  };

  const getSelectedCategory = () => {
    if (!formData.category_id) return null;
    return categories.find(cat => cat.id === formData.category_id);
  };

  const selectedCategory = getSelectedCategory();

  const handleManageCategories = () => {
    setShowCategoryDropdown(false);
    setShowCategoryManager(true);
  };

  const handleCloseCategoryManager = () => {
    setShowCategoryManager(false);
    fetchCategories(); // 刷新分类列表
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        <h2 style={{ marginTop: 0, color: 'var(--color-text-primary)' }}>
          {memoToEdit ? '编辑备忘录' : '新建备忘录'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label htmlFor="title" style={styles.label}>标题 *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="输入标题..."
              style={styles.input}
              autoFocus
            />
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>分类</label>
            <div style={{ position: 'relative' }}>
              <div 
                style={styles.categorySelector}
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              >
                {selectedCategory ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>{selectedCategory.icon}</span>
                    <span>{selectedCategory.name}</span>
                    <div 
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: selectedCategory.color,
                        marginLeft: 'auto'
                      }}
                    />
                  </div>
                ) : (
                  <span style={{ color: 'var(--color-text-tertiary)' }}>
                    选择分类（可选）
                  </span>
                )}
                <span style={{ marginLeft: 'auto' }}>▼</span>
              </div>
              
              {showCategoryDropdown && (
                <div style={styles.dropdown}>
                  <div style={styles.dropdownHeader}>
                    <span style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                      选择分类
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowCategoryDropdown(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: 'var(--color-text-tertiary)'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div style={styles.categoryList}>
                    {loadingCategories ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <div style={styles.spinner}></div>
                      </div>
                    ) : categories.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                        还没有分类，请先创建分类
                      </div>
                    ) : (
                      <>
                        <div
                          style={styles.categoryItem}
                          onClick={() => handleCategorySelect(null)}
                        >
                          <span style={{ fontSize: '18px' }}>📄</span>
                          <span>未分类</span>
                        </div>
                        {categories.map(category => (
                          <div
                            key={category.id}
                            style={styles.categoryItem}
                            onClick={() => handleCategorySelect(category.id)}
                          >
                            <span style={{ fontSize: '18px' }}>{category.icon}</span>
                            <span>{category.name}</span>
                            <div 
                              style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: category.color,
                                marginLeft: 'auto'
                              }}
                            />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  
                  <div style={styles.dropdownFooter}>
                    <button
                      type="button"
                      onClick={handleManageCategories}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-accent)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '5px 10px'
                      }}
                    >
                      管理分类
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <label htmlFor="content" style={styles.label}>内容</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="输入内容（支持多行）..."
              style={styles.textarea}
              rows="8"
            />
          </div>
          <div style={styles.buttonGroup}>
            <button type="submit" style={{...styles.button, ...styles.saveButton}}>
              {memoToEdit ? '更新' : '保存'}
            </button>
            <button 
              type="button" 
              onClick={onCancel}
              style={{...styles.button, ...styles.cancelButton}}
            >
              取消
            </button>
          </div>
        </form>
      </div>

      {/* 分类管理模态框 */}
      {showCategoryManager && (
        <div style={styles.modalOverlay} onClick={handleCloseCategoryManager}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-text-primary)' }}>
              管理分类
            </h2>
            
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
              {categories.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--color-text-tertiary)' }}>
                  <p>还没有创建任何分类</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {categories.map(category => (
                    <div key={category.id} style={styles.categoryListItem}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>{category.icon}</span>
                        <span style={{ flex: 1 }}>{category.name}</span>
                        <div 
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: category.color
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={handleCloseCategoryManager}
                style={{...styles.button, ...styles.cancelButton}}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  dialog: {
    background: 'var(--color-bg-primary)',
    borderRadius: '12px',
    padding: '30px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    border: '1px solid var(--color-border-primary)',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  modalContent: {
    background: 'var(--color-bg-primary)',
    borderRadius: '12px',
    padding: '30px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    border: '1px solid var(--color-border-primary)',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: 'var(--color-text-primary)',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid var(--color-border-primary)',
    borderRadius: '8px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-bg-secondary)',
    color: 'var(--color-text-primary)',
  },
  categorySelector: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid var(--color-border-primary)',
    borderRadius: '8px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-bg-secondary)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-primary)',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    zIndex: 1001,
    marginTop: '4px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  dropdownHeader: {
    padding: '12px',
    borderBottom: '1px solid var(--color-border-primary)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  categoryList: {
    padding: '8px 0',
  },
  categoryListItem: {
    padding: '15px',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: '8px',
    border: '1px solid var(--color-border-primary)',
  },
  categoryItem: {
    padding: '10px 15px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'background-color 0.2s',
  },
  categoryItemHover: {
    backgroundColor: 'var(--color-bg-secondary)',
  },
  dropdownFooter: {
    padding: '12px',
    borderTop: '1px solid var(--color-border-primary)',
    textAlign: 'center',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid var(--color-border-primary)',
    borderRadius: '8px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '200px',
    backgroundColor: 'var(--color-bg-secondary)',
    color: 'var(--color-text-primary)',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  button: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  saveButton: {
    backgroundColor: 'var(--color-btn-primary)',
    color: 'white',
  },
  cancelButton: {
    backgroundColor: 'var(--color-btn-secondary)',
    color: 'var(--color-text-primary)',
  },
  spinner: {
    width: '24px',
    height: '24px',
    border: '3px solid var(--color-border-primary)',
    borderTopColor: 'var(--color-accent)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto',
  },
};

export default MemoEditor;